import AppDispatcher from '../dispatcher/AppDispatcher';
import HotelConstants from '../constants/HotelConstants';
import LazyRefreshConstants from '../constants/LazyRefreshConstants';
import DealConstants from '../constants/DealConstants';
import ResultsStore from '../stores/ResultsStore';
import ProfileStore from '../stores/ProfileStore';
import LazyRecheckStore from '../stores/LazyRecheckStore';
import RecheckAlternative from 'arbitrip-common/client/utils/RecheckAlternative';
import Ajax from '../utils/Ajax';
import { HTTP_STATUS_CODE } from '@arbitrip/constants';

// Once per (search token, hotel) — the server enforces the same and answers 409.
const _refreshed_keys = new Set();
let _refreshed_keys_token = null;

// Keys only ever matter for the current search, so drop the previous search's keys when it changes.
function resetRefreshedKeysOnNewSearch(token) {
    if (token !== _refreshed_keys_token) {
        _refreshed_keys.clear();
        _refreshed_keys_token = token;
    }
}

function getLazyRecheckFlags() {
    return RecheckAlternative.getLazyRecheckFlags(ProfileStore.getProfile()?.company?.settings);
}

function getRecheckToken() {
    return LazyRecheckStore.getRecheckToken();
}

function getRefreshableDealIds(hotel_id) {
    const all_deals = ResultsStore.getHotelDealsById(hotel_id) || [];
    const excluded_ids = new Set([
        ...(ResultsStore.getBannedDealIds() || []),
        ...LazyRecheckStore.getDealsToBan().map((d) => d.id),
    ]);
    return all_deals
        .filter(
            (d) =>
                d.is_lazy_recheck &&
                !excluded_ids.has(d.id) &&
                !d.not_available &&
                !d.ignore &&
                d.status !== DealConstants.DEAL_STATUS.NA,
        )
        .map((d) => d.id);
}

const LazyRecheckActions = {
    start: function (clicked_deal, hotel_id) {
        const token = getRecheckToken();
        const all_deals = ResultsStore.getHotelDealsById(hotel_id) || [];
        const { changes_detected, suggest_alternative } = getLazyRecheckFlags();

        const lazy_peer_deals = suggest_alternative
            ? all_deals.filter(
                  (d) => d.superseded_group_id === clicked_deal.superseded_group_id && d.id !== clicked_deal.id,
              )
            : [];

        const all_lazy_deals = [clicked_deal, ...lazy_peer_deals];
        const all_lazy_ids = all_lazy_deals.filter((d) => d.is_lazy_recheck).map((d) => d.id);

        const alternatives_snapshot = RecheckAlternative.buildAlternativesSnapshot({
            clicked_deal,
            candidate_deals: all_deals,
        });

        AppDispatcher.handleAction({
            actionType: HotelConstants.LAZY_RECHECK_START,
            data: {
                clicked_deal,
                lazy_deals: all_lazy_deals,
                hotel_id,
                changes_detected,
                suggest_alternative,
                alternatives_snapshot,
            },
        });

        Ajax.lazyRecheckDeals({ token, hotel_id, deal_ids: all_lazy_ids })
            .done(function (res) {
                AppDispatcher.handleAction({
                    actionType: HotelConstants.LAZY_RECHECK_REQUESTED,
                    data: { listener_id: res.listener_id, wait_time: res.wait_time },
                });
            })
            .fail(function (err) {
                console.error('LazyRecheckActions.start failed', err);
                if (err?.status === 410) {
                    AppDispatcher.handleAction({
                        actionType: HotelConstants.LAZY_RECHECK_EXPIRED,
                    });
                    return;
                }
                AppDispatcher.handleAction({
                    actionType: HotelConstants.LAZY_RECHECK_NO_AVAILABILITY,
                    data: {},
                });
            });
    },

    startRefresh: function ({ hotel_id, trigger_popup, search_id, hotel, user }) {
        const token = getRecheckToken();
        if (!token || !hotel_id) {
            return;
        }
        resetRefreshedKeysOnNewSearch(token);
        const refresh_key = `${token}:${hotel_id}`;
        if (_refreshed_keys.has(refresh_key)) {
            return;
        }

        const deal_ids = getRefreshableDealIds(hotel_id);
        if (deal_ids.length === 0) {
            return;
        }
        _refreshed_keys.add(refresh_key);

        const { changes_detected, suggest_alternative } = getLazyRecheckFlags();

        AppDispatcher.handleAction({
            actionType: LazyRefreshConstants.LAZY_REFRESH_START,
            data: {
                refresh_key,
                token,
                hotel_id,
                deal_ids,
                trigger_popup,
                search_id,
                hotel,
                user,
                changes_detected,
                suggest_alternative,
            },
        });

        Ajax.lazyRefreshDeals({ token, hotel_id, deal_ids })
            .done(function (res) {
                AppDispatcher.handleAction({
                    actionType: LazyRefreshConstants.LAZY_REFRESH_REQUESTED,
                    data: { refresh_key, listener_id: res.listener_id, wait_time: res.wait_time },
                });
            })
            .fail(function (err) {
                // 409: the server already refreshed this hotel for this search — stay silent.
                // Anything else never ran a refresh, so a later popup may retry it.
                if (err?.status !== HTTP_STATUS_CODE._409_) {
                    console.error('LazyRecheckActions.startRefresh failed', err);
                    _refreshed_keys.delete(refresh_key);
                }
                AppDispatcher.handleAction({
                    actionType: LazyRefreshConstants.LAZY_REFRESH_FAILED,
                    data: { refresh_key },
                });
            });
    },

    reset: function () {
        AppDispatcher.handleAction({
            actionType: HotelConstants.LAZY_RECHECK_RESET,
            data: {},
        });
    },

    confirmPriceChange: function () {
        AppDispatcher.handleAction({
            actionType: HotelConstants.LAZY_RECHECK_CONFIRM_PRICE_CHANGE,
            data: {},
        });
    },
};

export default LazyRecheckActions;
